import React from "react";
import { Modal, Button, Typography } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

const { Text } = Typography;

interface UnpaidExpensesModalProps {
  visible: boolean;
  onClose: () => void;
  matrix: any;
  locationFilters: string[];
  locationMap: Record<string, string>;
  onCustomEntryStatusChange: (item: any, newStatus: string) => void;
  onDailyRevenueStatusChange: (item: any, newStatus: string) => void;
  onDefaultValueStatusChange: (item: any, newStatus: string) => void;
}

const UnpaidExpensesModal: React.FC<UnpaidExpensesModalProps> = ({
  visible,
  onClose,
  matrix,
  locationFilters,
  locationMap,
  onCustomEntryStatusChange,
  onDailyRevenueStatusChange,
  onDefaultValueStatusChange,
}) => {
  const { t } = useTranslation();

  console.log("matrix", matrix);
  const renderUnpaidItems = () => {
    if (!matrix?.rows) return null;

    const isGlobal = !locationFilters.length || locationFilters[0] === "";
    const activeLoc = !isGlobal ? locationFilters[0] : null;

    return matrix.rows.map((row: any) => {
      const grouped: Record<string, any[]> = {};
      // Prikazuj SVE suppliers (i sa i bez locationId)
      (row.suppliers || []).forEach((sup: any) => {
        if (!grouped[sup.supplierId]) grouped[sup.supplierId] = [];
        grouped[sup.supplierId].push(sup);
      });

      const unpaidItems: any[] = [];

      Object.values(grouped).forEach((entries: any[]) => {
        let chosen: any[] = entries;
        if (activeLoc) {
          // SPECIFIC LOCATION: Include ONLY entries for that location (exclude global)
          const locEntries = entries.filter(
            (e) => (e.locationId || "").toString() === activeLoc
          );
          chosen = locEntries;
        } else {
          // GLOBAL: uključi SAMO unose SA locationId (ne računamo global)
          chosen = entries.filter((e) => e.locationId);
        }

        chosen.forEach((sup: any) => {
          // Default value - check payment status
          const defaultValue = Number(sup.defaultValue) || 0;
          const defaultStatus = sup.defaultPaymentStatus || "unpaid";

          if (
            defaultValue > 0 &&
            (defaultStatus === "unpaid" || defaultStatus === "partial")
          ) {
            const locationIdStr = sup.locationId
              ? sup.locationId.toString()
              : null;
            const locationName =
              locationIdStr && locationIdStr !== "null"
                ? locationMap[locationIdStr] || t("Global")
                : t("Global");

            unpaidItems.push({
              type: "default",
              categoryName: row.categoryName,
              supplierName: sup.supplierName,
              locationName,
              description: t("Fixed monthly cost"),
              amount: defaultValue,
              status: defaultStatus,
              paidAmount: sup.defaultPartialPayment?.paidAmount || 0,
              remainingAmount:
                defaultStatus === "partial"
                  ? sup.defaultPartialPayment?.remainingAmount || 0
                  : defaultValue,
              categoryId: row.categoryId,
              supplierId: sup.supplierId,
              locationId: sup.locationId,
            });
          }

          // Custom entries
          if (sup.customEntries && Array.isArray(sup.customEntries)) {
            sup.customEntries.forEach((entry: any, idx: number) => {
              const entryStatus = entry.paymentStatus || "unpaid";
              const entryAmount = Number(entry.amount) || 0;

              if (entryStatus === "unpaid" || entryStatus === "partial") {
                const locationIdStr = sup.locationId
                  ? sup.locationId.toString()
                  : null;
                const locationName =
                  locationIdStr && locationIdStr !== "null"
                    ? locationMap[locationIdStr] || t("Global")
                    : t("Global");

                unpaidItems.push({
                  type: "custom",
                  categoryName: row.categoryName,
                  supplierName: sup.supplierName,
                  locationName,
                  description: entry.description || "-",
                  amount: entryAmount,
                  status: entryStatus,
                  paidAmount: entry.partialPayment?.paidAmount || 0,
                  remainingAmount:
                    entryStatus === "partial"
                      ? entry.partialPayment?.remainingAmount || 0
                      : entryAmount,
                  categoryId: row.categoryId,
                  supplierId: sup.supplierId,
                  locationId: sup.locationId,
                  entryIndex: idx,
                });
              }
            });
          }

          // Daily revenue entries
          if (
            sup.dailyRevenueEntries &&
            Array.isArray(sup.dailyRevenueEntries)
          ) {
            sup.dailyRevenueEntries.forEach((entry: any) => {
              const entryStatus = entry.paymentStatus || "paid";
              const entryAmount = Number(entry.amount) || 0;

              if (entryStatus === "unpaid" || entryStatus === "partial") {
                const locationIdStr = sup.locationId
                  ? sup.locationId.toString()
                  : null;
                const locationName =
                  locationIdStr && locationIdStr !== "null"
                    ? locationMap[locationIdStr] || t("Global")
                    : t("Global");

                unpaidItems.push({
                  type: "dailyRevenue",
                  categoryName: row.categoryName,
                  supplierName: sup.supplierName,
                  locationName,
                  description: dayjs(entry.date).format("DD.MM.YYYY"),
                  amount: entryAmount,
                  status: entryStatus,
                  paidAmount: entry.partialPayment?.paidAmount || 0,
                  remainingAmount:
                    entryStatus === "partial"
                      ? entry.partialPayment?.remainingAmount || 0
                      : entryAmount,
                  categoryId: row.categoryId,
                  supplierId: sup.supplierId,
                  locationId: sup.locationId,
                  date: entry.date,
                  entryId: entry._id || null,
                });
              }
            });
          }
        });
      });

      if (unpaidItems.length === 0) return null;

      // Deduplicate daily revenue entries by entryId (same entry can appear in both global and location-specific suppliers)
      const seenEntryIds = new Set<string>();
      const deduplicatedItems = unpaidItems.filter((item) => {
        if (item.type === "dailyRevenue" && item.entryId) {
          if (seenEntryIds.has(item.entryId)) {
            return false; // Skip duplicate
          }
          seenEntryIds.add(item.entryId);
        }
        return true; // Keep custom entries and unique daily revenue entries
      });

      return (
        <div key={row.categoryId} style={{ marginBottom: 24 }}>
          <Text
            strong
            style={{
              fontSize: 16,
              color: "#262626",
              display: "block",
              marginBottom: 12,
            }}
          >
            {row.categoryName}
          </Text>
          {deduplicatedItems.map((item, idx) => (
            <div
              key={`${item.type}-${idx}`}
              style={{
                padding: "12px 16px",
                background: "#fafafa",
                borderRadius: 8,
                marginBottom: 8,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div style={{ flex: 1 }}>
                <Text strong style={{ display: "block", fontSize: 14 }}>
                  {item.supplierName} · {item.locationName}
                </Text>
                <Text
                  type="secondary"
                  style={{ fontSize: 13, display: "block" }}
                >
                  {item.description}
                </Text>
              </div>
              <div
                style={{
                  textAlign: "right",
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                }}
              >
                <div>
                  {item.status === "partial" && (
                    <Text
                      type="secondary"
                      style={{ fontSize: 12, display: "block" }}
                    >
                      {t("Paid")}: {item.paidAmount.toLocaleString("sr-RS")} RSD
                    </Text>
                  )}
                  <Text
                    strong
                    style={{
                      fontSize: 15,
                      color: item.status === "unpaid" ? "#ff4d4f" : "#faad14",
                    }}
                  >
                    {item.remainingAmount.toLocaleString("sr-RS")} RSD
                  </Text>
                </div>

                {/* Payment Status Circles */}
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  {/* Paid Circle */}
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      if (item.type === "custom") {
                        onCustomEntryStatusChange(item, "paid");
                      } else if (item.type === "dailyRevenue") {
                        onDailyRevenueStatusChange(item, "paid");
                      } else if (item.type === "default") {
                        onDefaultValueStatusChange(item, "paid");
                      }
                    }}
                    style={{
                      width: 20,
                      height: 20,
                      borderRadius: "50%",
                      border:
                        item.status === "paid"
                          ? "2px solid #52c41a"
                          : "2px solid #d9d9d9",
                      background:
                        item.status === "paid" ? "#52c41a" : "transparent",
                      cursor: "pointer",
                      transition: "all 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      if (item.status !== "paid") {
                        e.currentTarget.style.borderColor = "#52c41a";
                        e.currentTarget.style.background =
                          "rgba(82, 196, 26, 0.1)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (item.status !== "paid") {
                        e.currentTarget.style.borderColor = "#d9d9d9";
                        e.currentTarget.style.background = "transparent";
                      }
                    }}
                  />

                  {/* Unpaid Circle */}
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      if (item.type === "custom") {
                        onCustomEntryStatusChange(item, "unpaid");
                      } else if (item.type === "dailyRevenue") {
                        onDailyRevenueStatusChange(item, "unpaid");
                      } else if (item.type === "default") {
                        onDefaultValueStatusChange(item, "unpaid");
                      }
                    }}
                    style={{
                      width: 20,
                      height: 20,
                      borderRadius: "50%",
                      border:
                        item.status === "unpaid"
                          ? "2px solid #ff4d4f"
                          : "2px solid #d9d9d9",
                      background:
                        item.status === "unpaid" ? "#ff4d4f" : "transparent",
                      cursor: "pointer",
                      transition: "all 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      if (item.status !== "unpaid") {
                        e.currentTarget.style.borderColor = "#ff4d4f";
                        e.currentTarget.style.background =
                          "rgba(255, 77, 79, 0.1)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (item.status !== "unpaid") {
                        e.currentTarget.style.borderColor = "#d9d9d9";
                        e.currentTarget.style.background = "transparent";
                      }
                    }}
                  />

                  {/* Partial Circle */}
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      if (item.type === "custom") {
                        onCustomEntryStatusChange(item, "partial");
                      } else if (item.type === "dailyRevenue") {
                        onDailyRevenueStatusChange(item, "partial");
                      } else if (item.type === "default") {
                        onDefaultValueStatusChange(item, "partial");
                      }
                    }}
                    style={{
                      width: 20,
                      height: 20,
                      borderRadius: "50%",
                      border:
                        item.status === "partial"
                          ? "2px solid #faad14"
                          : "2px solid #d9d9d9",
                      background:
                        item.status === "partial" ? "#faad14" : "transparent",
                      cursor: "pointer",
                      transition: "all 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      if (item.status !== "partial") {
                        e.currentTarget.style.borderColor = "#faad14";
                        e.currentTarget.style.background =
                          "rgba(250, 173, 20, 0.1)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (item.status !== "partial") {
                        e.currentTarget.style.borderColor = "#d9d9d9";
                        e.currentTarget.style.background = "transparent";
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    });
  };

  const hasUnpaidItems = matrix?.rows?.some((row: any) => {
    const grouped: Record<string, any[]> = {};
    (row.suppliers || []).forEach((sup: any) => {
      if (!grouped[sup.supplierId]) grouped[sup.supplierId] = [];
      grouped[sup.supplierId].push(sup);
    });

    return Object.values(grouped).some((entries: any[]) =>
      entries.some(
        (sup: any) =>
          sup.customEntries?.some(
            (e: any) =>
              e.paymentStatus === "unpaid" || e.paymentStatus === "partial"
          ) ||
          sup.dailyRevenueEntries?.some(
            (e: any) =>
              e.paymentStatus === "unpaid" || e.paymentStatus === "partial"
          )
      )
    );
  });

  return (
    <Modal
      className="standardHeader standardFooter"
      title={<div>{t("Unpaid and partially unpaid expenses")}</div>}
      open={visible}
      onCancel={onClose}
      footer={
        <div>
          <Button onClick={onClose}>{t("Close")}</Button>
        </div>
      }
      width={900}
    >
      <div style={{ maxHeight: "70vh", overflowY: "auto" }}>
        {renderUnpaidItems()}

        {!hasUnpaidItems && (
          <div
            style={{
              textAlign: "center",
              padding: "40px 20px",
              color: "#8c8c8c",
            }}
          >
            <Text type="secondary">{t("No unpaid expenses")}</Text>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default UnpaidExpensesModal;
